import { useEffect, useRef } from 'react'

// Story 175, CU11/CU12 (RN44-RN47, contract §2/§7) -- the two marks the Gantt draws on the border of a job
// bar, both decided by its FABRICATION ORDER and never by the job itself:
// - late: the order is `LATE` in Sun's delivery classification (`GET planMetrics/{id}/deliveries`, RN44).
//   Jupiter compares nothing: an order that is not in the answer, or not `LATE`, or an answer that never
//   came, is simply not marked (RN27, RN56, INV-I).
// - selected: the order is the ONE selected fabrication order of the Planner (RN45, INV-H), and this mark
//   wins over the late one (RN46, DL-9).
// The fill colours of the bars are not touched (RN47).
export const LATE_FO_CLASS = 'planner-fo-late'
export const SELECTED_FO_CLASS = 'planner-fo-selected'
// The red of the retired per-task border, kept for the per-order one so the colour users know keeps its place.
export const LATE_FO_BORDER_COLOR = '#FF0000'
// The selection reads like a macOS selection: the system blue as a thin ring, no glow. Calendar.jsx draws the
// ring and steps every other bar back.
export const SELECTED_FO_BORDER_COLOR = '#0A84FF'
// Set on the calendar wrapper while an order is selected, so the other bars step back (see Calendar.jsx).
export const HAS_SELECTED_FO_CLASS = 'planner-has-selected-fo'

const NO_MARKS = []
const LATE_MARKS = [LATE_FO_CLASS]
const SELECTED_MARKS = [SELECTED_FO_CLASS]

const isSelected = (fabricationOrderId, selectedFabricationOrderId) =>
  selectedFabricationOrderId !== null && selectedFabricationOrderId !== undefined && fabricationOrderId === Number(selectedFabricationOrderId)
export const NO_LATE_FABRICATION_ORDERS = new Set()

/**
 * The ids of the `LATE` fabrication orders of a `/deliveries` answer, exactly as Sun classified them.
 * @param {Object|null} deliveries The §2 answer, or null while there is none (loading, failed, folded)
 * @returns {Set<number>} The late fabrication order ids
 */
export const lateFabricationOrderIdsOf = (deliveries) => {
  if (!deliveries || !Array.isArray(deliveries.fabricationOrders)) {
    return NO_LATE_FABRICATION_ORDERS
  }
  return new Set(deliveries.fabricationOrders
    .filter((fabricationOrder) => fabricationOrder.deliveryStatus === 'LATE')
    .map((fabricationOrder) => fabricationOrder.fabricationOrderId))
}

/**
 * The border class of one calendar entry: only job bars carry a fabrication order mark.
 * @param {Object} event FullCalendar event (only `extendedProps` is read)
 * @param {Set<number>} lateFabricationOrderIds Ids of the `LATE` orders
 * @param {number|null} selectedFabricationOrderId The one selected order of the Planner, null when none
 * @returns {string[]} The class names to add to the bar
 */
export const fabricationOrderMarksOf = (event, lateFabricationOrderIds, selectedFabricationOrderId) => {
  const extendedProps = event?.extendedProps
  if (!extendedProps?.jobEvent) {
    return NO_MARKS
  }
  const fabricationOrderId = Number(extendedProps.fabricationOrderId)
  // RN46 / DL-9: the explicit choice of the user wins; the delay stays visible in the table and the drawer.
  if (isSelected(fabricationOrderId, selectedFabricationOrderId)) {
    return SELECTED_MARKS
  }
  if (lateFabricationOrderIds.has(fabricationOrderId)) {
    return LATE_MARKS
  }
  return NO_MARKS
}

const classifiedIdsOf = (deliveries) => new Set(deliveries.fabricationOrders.map((fabricationOrder) => fabricationOrder.fabricationOrderId))

/**
 * RN45 (S146) -- tells the Planner when its selected fabrication order has left the plan that is on screen.
 * The source is the Gantt's own `/deliveries` answer (same global filters): an order that WAS classified and
 * is missing from the next answer of the same plan -- a global filter no longer includes it, or a write took
 * it out of the plan -- is gone. An order that was never there (no classification, RN27) is not "gone", and
 * a failed read (no answer) proves nothing (RN56). Changing plan is the Planner's own business.
 * @param {Object} options Options
 * @param {Object|null} options.deliveries The latest §2 answer, or null
 * @param {number|null} options.selectedFabricationOrderId The selected order, null when none
 * @param {function()} options.onGone Called once when the selected order leaves the answer
 */
export const useSelectedFabricationOrderPresence = ({ deliveries, selectedFabricationOrderId, onGone }) => {
  const previousAnswerRef = useRef(null)
  const selectedRef = useRef(selectedFabricationOrderId)
  selectedRef.current = selectedFabricationOrderId
  const onGoneRef = useRef(onGone)
  onGoneRef.current = onGone

  useEffect(() => {
    if (!deliveries || !Array.isArray(deliveries.fabricationOrders)) {
      return
    }
    const previous = previousAnswerRef.current
    const current = { planificationId: deliveries.planificationId, ids: classifiedIdsOf(deliveries) }
    previousAnswerRef.current = current
    const selectedId = selectedRef.current
    if (!previous || previous.planificationId !== current.planificationId || selectedId === null || selectedId === undefined) {
      return
    }
    if (previous.ids.has(selectedId) && !current.ids.has(selectedId) && typeof onGoneRef.current === 'function') {
      onGoneRef.current()
    }
  }, [deliveries])
}
